<template>
  <div class="balansir-editor">
    <h5>{{ isNew ? 'Новая запись' : 'Запись: ' + form.name }}</h5>

    <label>Имя (группа)</label>
    <input v-model="form.name" type="text" class="form-control" placeholder="Например: экспертиза"/>

    <label>UTM-фразы</label>
    <input ref="tagsInput" class="form-control" placeholder="Добавьте фразы"/>

    <label>URL лендинга</label>
    <input v-model="form.landingUrl" type="text" class="form-control" placeholder="https://landing-page.trends-ds.com/index5"/>

    <label>Трекер A (urlA)</label>
    <input v-model="form.urlA" list="trackerOptionsList" type="text" class="form-control" placeholder="Выберите токен или вставьте URL"/>
    <label>Трекер B (urlB)</label>
    <input v-model="form.urlB" list="trackerOptionsList" type="text" class="form-control" placeholder="Выберите токен или вставьте URL"/>
    <datalist id="trackerOptionsList">
      <option v-for="opt in trackerOptions" :key="opt.url" :value="opt.url">{{ opt.label }}</option>
    </datalist>

    <div v-for="variant in ['A', 'B']" :key="variant" class="rules-block">
      <div class="rules-head">
        <b>Правила {{ variant }}</b>
        <button v-if="variant === 'B'" class="btn btn-sm btn-outline-secondary" @click="copyAtoB">Копировать A → B</button>
      </div>
      <table class="table table-sm">
        <thead>
          <tr><th style="width:35%">CSS-селектор</th><th style="width:15%">Роль</th><th style="width:20%">Действие</th><th>Значение</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="(rule, idx) in rules[variant]" :key="idx">
            <td><input v-model="rule.selector" type="text" class="form-control form-control-sm" placeholder="#rec123 > div"/></td>
            <td>
              <select v-model="rule.role" class="form-select form-select-sm">
                <option value="">прочее</option>
                <option value="title">заголовок</option>
                <option value="subtitle">подзаголовок</option>
              </select>
            </td>
            <td>
              <select v-model="rule.action" class="form-select form-select-sm">
                <option value="text">Заменить текст</option>
                <option value="html">Заменить HTML</option>
                <option value="hidden">Скрыть блок</option>
              </select>
            </td>
            <td>
              <textarea v-if="rule.action !== 'hidden'" v-model="rule.value" rows="2" class="form-control form-control-sm"></textarea>
              <span v-else class="text-muted">—</span>
            </td>
            <td><button class="btn btn-sm btn-outline-danger" @click="rules[variant].splice(idx, 1)">✕</button></td>
          </tr>
        </tbody>
      </table>
      <button class="btn btn-sm btn-outline-primary" @click="rules[variant].push({selector: '', role: '', action: 'text', value: ''})">+ правило</button>
    </div>

    <div class="editor-actions">
      <button class="btn btn-primary" @click="save" :disabled="saving">{{ saving ? 'Сохраняем…' : 'Сохранить' }}</button>
      <button v-if="!isNew" class="btn btn-outline-secondary" @click="duplicate">Дублировать</button>
      <button v-if="!isNew" class="btn btn-outline-danger" @click="remove">Удалить</button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Tagify from '@yaireo/tagify';

function toRules(blockReWrite) {
  return (blockReWrite || []).map(item => {
    const role = item.role || '';
    if (item.hidden) return { selector: item.elementSelectorAll || '', role, action: 'hidden', value: '' };
    if (item.innerHTML) return { selector: item.elementSelectorAll || '', role, action: 'html', value: item.innerHTML };
    return { selector: item.elementSelectorAll || '', role, action: 'text', value: item.text || '' };
  });
}

function toBlockReWrite(rules) {
  return rules
    .filter(r => r.selector && r.selector.trim() !== '')
    .map(r => {
      if (r.action === 'hidden') return { elementSelectorAll: r.selector.trim(), hidden: true, role: r.role || '' };
      if (r.action === 'html') return { elementSelectorAll: r.selector.trim(), text: '', innerHTML: r.value || '', role: r.role || '' };
      return { elementSelectorAll: r.selector.trim(), text: r.value || '', innerHTML: '', role: r.role || '' };
    });
}

export default {
  props: {
    record: { type: Object, required: true },
    isNew: { type: Boolean, default: false },
    trackerOptions: { type: Array, default: () => [] },
    allRecords: { type: Array, default: () => [] },
  },
  emits: ['saved', 'deleted', 'duplicate'],
  data() {
    return {
      form: { name: '', landingUrl: '', urlA: '', urlB: '' },
      utm: [],
      rules: { A: [], B: [] },
      saving: false,
      hostApi: process.env.VUE_APP_API_URL ? process.env.VUE_APP_API_URL : 'http://localhost:5001',
    };
  },
  watch: {
    record: {
      immediate: true,
      handler() { this.initFromRecord(); },
    },
  },
  mounted() {
    this.tagify = new Tagify(this.$refs.tagsInput, { whitelist: [] });
    this.tagify.addTags(this.utm);
    this.tagify.on('change', () => {
      this.utm = this.tagify.value.map(t => t.value);
    });
  },
  beforeUnmount() {
    if (this.tagify) this.tagify.destroy();
  },
  methods: {
    initFromRecord() {
      this.form = {
        name: this.record.name || '',
        landingUrl: this.record.landingUrl || '',
        urlA: this.record.urlA || '',
        urlB: this.record.urlB || '',
      };
      this.utm = (this.record.utm || []).slice();
      this.rules = {
        A: toRules(this.record.blockReWriteA),
        B: toRules(this.record.blockReWriteB),
      };
      if (this.tagify) {
        this.tagify.removeAllTags();
        this.tagify.addTags(this.utm);
      }
    },
    copyAtoB() {
      this.rules.B = JSON.parse(JSON.stringify(this.rules.A));
    },
    buildPayload() {
      return {
        groupId: this.record.groupId,
        name: this.form.name,
        landingUrl: this.form.landingUrl,
        utm: this.utm,
        urlA: this.form.urlA,
        urlB: this.form.urlB,
        blockReWriteA: toBlockReWrite(this.rules.A),
        blockReWriteB: toBlockReWrite(this.rules.B),
      };
    },
    validate() {
      if (!this.form.name || this.form.name.trim() === '') {
        alert('Имя записи не может быть пустым'); return false;
      }
      if (this.utm.length === 0) {
        alert('Добавьте хотя бы одну UTM-фразу'); return false;
      }
      const emptySel = [...this.rules.A, ...this.rules.B].some(
        r => r.selector.trim() === '' && (r.action === 'hidden' || (r.value || '').trim() !== ''));
      if (emptySel) {
        alert('Есть правило без CSS-селектора — заполните или удалите строку'); return false;
      }
      // на роль «заголовок» / «подзаголовок» в варианте берётся одно правило, иначе показанное неоднозначно
      for (const v of ['A', 'B']) {
        for (const [role, name] of [['title', 'заголовка'], ['subtitle', 'подзаголовка']]) {
          if (this.rules[v].filter(r => r.selector.trim() !== '' && r.role === role).length > 1) {
            alert('В варианте ' + v + ' больше одного ' + name); return false;
          }
        }
      }
      // дубли фраз с другими записями (в Redis побеждает последняя — предупреждаем)
      const others = this.allRecords.filter(r => r.groupId !== this.record.groupId);
      const dup = this.utm.filter(u => others.some(r => r.utm.includes(u)));
      if (dup.length > 0) {
        if (!window.confirm('Фразы уже используются в других записях (в балансировщике останется одна из версий):\n' + dup.join('\n') + '\n\nВсё равно сохранить?')) return false;
      }
      return true;
    },
    save() {
      if (!this.validate()) return;
      let vm = this;
      vm.saving = true;
      const endpoint = vm.isNew ? '/api/v1/balansir/add' : '/api/v1/balansir/update';
      axios.post(vm.hostApi + endpoint, vm.buildPayload())
        .then(function (response) {
          vm.$emit('saved', response.data.data.id);
        })
        .catch(function (error) {
          console.log(error);
          alert('Ошибка сохранения: ' + (error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message));
        })
        .finally(function () { vm.saving = false; });
    },
    duplicate() {
      const copy = this.buildPayload();
      copy.groupId = '';
      this.$emit('duplicate', copy);
    },
    remove() {
      if (!window.confirm('Удалить запись «' + this.form.name + '» (' + this.utm.length + ' фраз)?')) return;
      let vm = this;
      axios.post(vm.hostApi + '/api/v1/balansir/delete', { groupId: vm.record.groupId })
        .then(function () { vm.$emit('deleted'); })
        .catch(function (error) {
          console.log(error);
          alert('Ошибка удаления: ' + (error.response && error.response.data && error.response.data.message ? error.response.data.message : error.message));
        });
    },
  },
};
</script>

<style scoped>
.balansir-editor { padding: 0 1em 3em 1em; max-height: 85vh; overflow-y: auto; }
.balansir-editor label { margin-top: 0.7em; font-weight: bold; }
.rules-block { margin-top: 1em; }
.rules-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.3em; }
.editor-actions { margin-top: 1.5em; display: flex; gap: 0.5em; }
</style>
